// Reusable Data Lab list table.
// Pass a collection name, column config, and per-row testid prefix; the rest is common.
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Search, ChevronRight, ArrowUpDown, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { get, asText } from "./_shared";
import { SourceDot, SOURCE_TONE } from "./_source";
import InfiniteSentinel from "../../components/InfiniteSentinel";
import AlphaRail from "../../components/AlphaRail";
import JumpRail from "../../components/JumpRail";

export default function LabListTable({
  collection,          // "orders" | "customers" | "persons" | "calls"
  columns,             // [{ key, label, path, sortable, w, fmt, className }]
  detailPath,          // (id) => string
  defaultSort,         // dot-path
  defaultOrder = "desc", // "asc" | "desc"
  searchPlaceholder = "Search…",
  testidPrefix,        // e.g. "lab-order"
  extraSearch = "",    // v2 pages append facet-chip selections to the search
  alphaField = null,   // dot-path of the name field → shows an A–Z rail
}) {
  const navigate = useNavigate();
  const LIMIT = 50;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState(defaultOrder);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [sourceCounts, setSourceCounts] = useState({ dev: 0, mongo: 0, manual: 0 });
  const [sourceFilter, setSourceFilter] = useState([]); // [] = all
  const [letter, setLetter] = useState(null);           // A–Z quick-jump
  const [jumpedPage, setJumpedPage] = useState(0);       // numeric jump position
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const genRef = useRef(0);

  const fetchAt = useCallback(async (page, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const gen = genRef.current;
    const effSort = letter && alphaField ? alphaField : sort;
    const effOrder = letter && alphaField ? "asc" : order;
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, sort: effSort, order: effOrder });
      const combined = [search.trim(), (extraSearch || "").trim()].filter(Boolean).join(" ");
      if (combined) params.set("search", combined);
      if (sourceFilter.length > 0) params.set("source", sourceFilter.join(","));
      if (letter && alphaField) params.set("starts_with", letter);
      const { data } = await api.get(`/lab/${collection}?${params.toString()}`);
      if (gen !== genRef.current) return;
      const batch = data.items || [];
      const t = data.total || 0;
      setItems((prev) => (append ? [...prev, ...batch] : batch));
      setTotal(t);
      if (data.source_counts) setSourceCounts(data.source_counts);
      pageRef.current = page;
      setHasMore(page * LIMIT < t);
    } catch (e) {
      if (gen === genRef.current && !append) { setItems([]); setTotal(0); }
    } finally {
      if (gen === genRef.current) { loadingRef.current = false; setLoading(false); }
    }
  }, [collection, sort, order, search, extraSearch, sourceFilter, letter, alphaField]);

  const load = useCallback(() => {
    genRef.current += 1;
    pageRef.current = 1;
    loadingRef.current = false;
    setHasMore(true);
    setJumpedPage(0);
    fetchAt(1, false);
  }, [fetchAt]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) fetchAt(pageRef.current + 1, true);
  }, [hasMore, fetchAt]);

  const jumpTo = useCallback((offset) => {
    const targetPage = Math.floor(offset / LIMIT) + 1;
    genRef.current += 1;
    pageRef.current = targetPage;
    loadingRef.current = false;
    setHasMore(true);
    setJumpedPage(targetPage);
    fetchAt(targetPage, false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [fetchAt]);

  const resetJump = useCallback(() => { setJumpedPage(0); load(); window.scrollTo({ top: 0, behavior: "auto" }); }, [load]);

  useEffect(() => { load(); }, [load]);

  // Row-level delete. Confirms first, then calls the admin-only endpoint.
  const handleDelete = useCallback(async (row) => {
    const id = row._id;
    const label = row.QuoteRef || row.name || row.FullName || row.Email || id;
    if (!window.confirm(`Delete this ${collection.replace(/s$/, "")} row?\n\n${label}\n\nThis cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/lab/${collection}/${encodeURIComponent(id)}`);
      setItems((prev) => prev.filter((r) => r._id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Delete failed";
      window.alert(`Could not delete row.\n\n${msg}`);
      load();
    } finally {
      setDeletingId(null);
    }
  }, [collection, load]);

  const toggleSort = (c) => {
    if (!c.sortable) return;
    setLetter(null);
    if (sort === c.path) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(c.path); setOrder("desc"); }
  };

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-list`}>
      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setLetter(null); }}
            placeholder={searchPlaceholder}
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#0F1420] border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-400/50"
            data-testid={`${testidPrefix}-search`}
          />
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid={`${testidPrefix}-refresh`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50"
          title="Reload from database"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Source filter chip row — Mongo (default) · Dev (grey) · Manual (blue) */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testidPrefix}-source-chips`}>
        <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 pr-1">Source</span>
        {[
          { key: "all",    label: "All",    count: (sourceCounts.dev || 0) + (sourceCounts.mongo || 0) + (sourceCounts.manual || 0) },
          { key: "mongo",  label: "Mongo",  count: sourceCounts.mongo  || 0, tone: SOURCE_TONE.mongo },
          { key: "dev",    label: "Dev",    count: sourceCounts.dev    || 0, tone: SOURCE_TONE.dev },
          { key: "manual", label: "Manual", count: sourceCounts.manual || 0, tone: SOURCE_TONE.manual },
        ].map((chip) => {
          const active = chip.key === "all"
            ? sourceFilter.length === 0
            : sourceFilter.length === 1 && sourceFilter[0] === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => { setSourceFilter(chip.key === "all" ? [] : [chip.key]); }}
              data-testid={`${testidPrefix}-source-${chip.key}`}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[10.5px] font-mono uppercase tracking-widest transition-colors ${
                active
                  ? "border-orange-400/40 bg-orange-500/10 text-orange-200"
                  : "border-white/10 bg-white/[0.02] text-slate-400 hover:text-white hover:border-white/20"
              }`}
            >
              {chip.tone && <span className={`inline-block w-1.5 h-1.5 rounded-full ${chip.tone.dot}`} />}
              {chip.label}
              <span className="tabular-nums text-slate-500">{chip.count}</span>
            </button>
          );
        })}
      </div>

      <div className="relative rounded-xl border border-white/[0.11] bg-[#111C34] overflow-hidden" style={{ boxShadow: "0 18px 40px -8px rgba(0,0,0,0.55), 0 4px 10px rgba(0,0,0,0.32), 0 0 0 1px rgba(90,130,220,0.04), inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -1px 0 rgba(0,0,0,0.30)" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-white/[0.04] border-b border-white/[0.08]">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c)}
                    className={`text-left px-3 py-3 text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-[#2FC8CB] font-mono ${c.w || ""} ${c.sortable ? "cursor-pointer hover:text-white" : ""}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.sortable && <ArrowUpDown className={`w-3 h-3 ${sort === c.path ? "text-[#C8A46B]" : "opacity-40"}`} />}
                    </span>
                  </th>
                ))}
                <th className="text-right px-3 py-3 w-24 text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-[#2FC8CB] font-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-3 py-10 text-center text-slate-500 text-sm">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-3 py-10 text-center text-slate-500 text-sm">No records found.</td></tr>
              )}
              {items.map((row) => (
                <tr
                  key={row._id}
                  onClick={() => navigate(detailPath(row._id))}
                  className="border-b border-white/[0.04] hover:bg-white/[0.04] cursor-pointer transition-colors"
                  data-testid={`${testidPrefix}-row-${row._id}`}
                >
                  {columns.map((c, colIdx) => {
                    const v = get(row, c.path);
                    const disp = c.fmt ? c.fmt(v, row) : asText(v);
                    return (
                      <td key={c.key} className={`px-3 py-3 ${c.className || "text-slate-300"}`}>
                        <span className="inline-flex items-center gap-2 min-w-0">
                          {colIdx === 0 && <SourceDot value={row._source} />}
                          <span className="line-clamp-1">{disp}</span>
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                        disabled={deletingId === row._id}
                        data-testid={`${testidPrefix}-delete-${row._id}`}
                        title="Delete this row"
                        className="p-1.5 rounded border border-rose-500/25 bg-rose-500/[0.06] text-rose-300 hover:bg-rose-500/[0.12] hover:text-rose-200 hover:border-rose-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {deletingId === row._id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length > 0 && (
          <InfiniteSentinel
            onReach={loadMore}
            hasMore={hasMore}
            loading={loading}
            count={items.length}
            total={total}
            testId={`${testidPrefix}-sentinel`}
          />
        )}
      </div>

      <div className="flex items-center justify-between text-sm text-slate-400">
        <div><span className="font-mono text-white">{items.length}</span> loaded · <span className="font-mono text-white" data-testid={`${testidPrefix}-total`}>{total}</span> total</div>
      </div>

      {alphaField ? (
        <AlphaRail active={letter} onPick={setLetter} testId={`${testidPrefix}-alpha-rail`} />
      ) : (
        <JumpRail total={total} atOffset={jumpedPage > 0 ? (jumpedPage - 1) * LIMIT : 0}
          onJump={jumpTo} onReset={resetJump} testId={`${testidPrefix}-jump-rail`} />
      )}
    </div>
  );
}
